import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateCourse = () => {
  const navigate = useNavigate();
  const { isTrainer } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    duration_hours: 4,
    level: 'beginner',
    image_url: '',
    objectives: [''],
    prerequisites: ['']
  });

  useEffect(() => {
    if (!isTrainer) {
      navigate('/dashboard');
    }
  }, [isTrainer, navigate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        objectives: formData.objectives.filter(o => o.trim()),
        prerequisites: formData.prerequisites.filter(p => p.trim())
      };

      const res = await fetch(`${API}/courses/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        credentials: 'include'
      });

      if (res.ok) {
        const course = await res.json();
        toast.success('Cours créé avec succès !');
        navigate(`/trainer/courses/${course.course_id}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création du cours');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    'Management',
    'Pédagogie',
    'Soft Skills',
    'Communication',
    'Leadership',
    'Numérique',
    'Développement personnel'
  ];

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="create-course-page">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>

        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">
            Créer un nouveau cours
          </h1>
          <p className="text-slate-600 mt-2">
            Remplissez les informations pour créer votre formation
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informations générales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titre du cours *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Ex: Leadership et Management Éducatif"
                  required
                  data-testid="title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Décrivez le contenu et les bénéfices de cette formation..."
                  rows={4}
                  required
                  data-testid="description-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Catégorie *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(v) => handleChange('category', v)}
                    required
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Sélectionner une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="level">Niveau *</Label>
                  <Select
                    value={formData.level}
                    onValueChange={(v) => handleChange('level', v)}
                  >
                    <SelectTrigger data-testid="level-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Débutant</SelectItem>
                      <SelectItem value="intermediate">Intermédiaire</SelectItem>
                      <SelectItem value="advanced">Avancé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duration">Durée (heures) *</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    value={formData.duration_hours}
                    onChange={(e) => handleChange('duration_hours', parseInt(e.target.value))}
                    required
                    data-testid="duration-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image">URL de l'image</Label>
                  <Input
                    id="image"
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => handleChange('image_url', e.target.value)}
                    placeholder="https://..."
                    data-testid="image-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Objectifs pédagogiques</CardTitle>
              <CardDescription>
                Listez ce que les apprenants sauront faire à la fin du cours
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {formData.objectives.map((obj, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={obj}
                    onChange={(e) => handleArrayChange('objectives', index, e.target.value)}
                    placeholder={`Objectif ${index + 1}`}
                    data-testid={`objective-${index}`}
                  />
                  {formData.objectives.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeArrayItem('objectives', index)}
                    >
                      ×
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => addArrayItem('objectives')}
                data-testid="add-objective-btn"
              >
                + Ajouter un objectif
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Prérequis</CardTitle>
              <CardDescription>
                Indiquez les connaissances ou compétences nécessaires (optionnel)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {formData.prerequisites.map((prereq, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={prereq}
                    onChange={(e) => handleArrayChange('prerequisites', index, e.target.value)}
                    placeholder={`Prérequis ${index + 1}`}
                    data-testid={`prerequisite-${index}`}
                  />
                  {formData.prerequisites.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeArrayItem('prerequisites', index)}
                    >
                      ×
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => addArrayItem('prerequisites')}
                data-testid="add-prerequisite-btn"
              >
                + Ajouter un prérequis
              </Button>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => navigate(-1)}
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={loading || !formData.title || !formData.description || !formData.category}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="create-course-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Créer le cours
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateCourse;
